
import React, { useRef, useEffect } from 'react';
import { Loader2, Trash2, ExternalLink, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Message } from '@/components/messages/types';

interface MessageListProps {
  messages: Message[];
  isLoading: boolean;
  formatMessageDate: (dateString: string) => string;
  handleDeleteMessage: (messageId: number) => void;
  currentUserId: string;
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  isLoading,
  formatMessageDate,
  handleDeleteMessage,
  currentUserId,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleImageClick = (imageUrl: string) => {
    window.open(imageUrl, '_blank');
  };

  return (
    <ScrollArea className="flex-1" type="auto">
      {isLoading ? (
        <div className="flex items-center justify-center h-full">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : messages.length === 0 ? (
        // Added padding here to match content padding
        <div className="text-center py-12 px-3 md:px-4"> 
          <p className="text-gray-500">Asnjë mesazh</p>
          <p className="text-sm text-gray-400">
            Dërgo një mesazh për të filluar bisedën
          </p>
        </div>
      ) : (
        // Added p-3 md:p-4 for padding around the message items, kept space-y
        <div className="p-3 md:p-4 space-y-3 md:space-y-4"> 
          {messages.map((message) => {
            const isOwnMessage = message.sender_id === currentUserId;
            
            return (
              <div
                key={message.id}
                className={`flex animate-scale-in ${isOwnMessage ? 'justify-end pl-6 sm:pl-10 md:pl-16' : 'justify-start pr-6 sm:pr-10 md:pr-16'}`}
              >
                <div className={`relative group shadow-sm ${isOwnMessage ? 'rounded-l-2xl rounded-tr-2xl' : 'rounded-r-2xl rounded-tl-2xl'}`}>
                  <div
                    className={`px-3 py-2 md:px-4 md:py-2.5 ${
                      isOwnMessage
                        ? 'bg-brand-500 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                    } ${isOwnMessage ? 'rounded-l-xl rounded-tr-xl' : 'rounded-r-xl rounded-tl-xl'}`}
                  >
                    {message.image_url && (
                      <div className="mb-2 relative">
                        <div 
                          className="flex items-center justify-between bg-black/10 dark:bg-white/10 rounded-lg p-2 cursor-pointer hover:bg-black/20 dark:hover:bg-white/20"
                          onClick={() => handleImageClick(message.image_url || '')}
                        >
                          <div className="flex items-center overflow-hidden">
                            <ImageIcon className="h-4 w-4 min-w-[16px] mr-2 flex-shrink-0" />
                            <span className="text-sm font-medium truncate">
                              Shiko foton
                            </span>
                          </div>
                          <ExternalLink className="h-4 w-4 ml-2 flex-shrink-0" />
                        </div>
                      </div>
                    )}
                    {message.content && (
                      <p className="break-words text-sm leading-relaxed">{message.content}</p>
                    )}
                    <p className={`text-xs mt-1.5 ${
                      isOwnMessage ? 'text-brand-200 dark:text-gray-400' : 'text-gray-500 dark:text-gray-400'
                    } text-right`}>
                      {formatMessageDate(message.created_at)}
                    </p>
                  </div>
                  
                  {isOwnMessage && (
                    <div className="absolute -left-7 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7 text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-500"
                        onClick={() => handleDeleteMessage(message.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      )}
    </ScrollArea>
  );
};

export default MessageList;
